import React, { useEffect, useRef, useState } from "react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import styles from "./PolicyEditor.module.css";
import { InfoTip } from "./InfoTip";
// ── Refresh status helpers ───────────────────────────────────────────────
const TERMINAL_STATUSES = new Set(["error", "refreshed"]);
const POLL_INTERVAL_MS = 2000;
const isRefreshableCredential = (c) => c.refresh !== undefined && c.refresh.strategy !== "static" && c.refresh.strategy !== "external";
const buildContribution = (refreshable, material, values) => {
    if (refreshable) {
        const keys = Object.keys(material);
        if (keys.length === 0)
            return undefined;
        return {
            credentials: keys.map((k) => ({ key: k, value: "UNSET" })),
            configures: keys.map((k) => ({ credential_key: k, material: material[k] }))
        };
    }
    if (!values || values.length === 0)
        return undefined;
    return { credentials: values, configures: [] };
};
// Parse an ISO-8601 UTC timestamp and return both a humanized, relative form
// (e.g. "3 minutes ago") and an absolute local form for a tooltip. Backend
// timestamps are UTC; if the string carries no timezone designator we assume
// UTC by appending "Z" so the relative time is computed correctly.
const humanizeTime = (iso) => {
    const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
    const date = parseISO(normalized);
    if (isNaN(date.getTime()))
        return undefined;
    return {
        relative: formatDistanceToNow(date, { addSuffix: true }),
        absolute: format(date, "MM/dd/yyyy, hh:mm aaa")
    };
};
const TimeValue = ({ iso, className }) => {
    const humanized = humanizeTime(iso);
    return humanized ? (React.createElement("span", { className: className, title: humanized.absolute }, humanized.relative)) : (React.createElement("span", { className: className }, iso));
};
// Poll a provider's refresh status until every entry reaches a terminal state.
function useProviderRefreshStatus(openShellClient, providerName, enabled) {
    const [status, setStatus] = useState([]);
    const [loading, setLoading] = useState(false);
    const pollTimerRef = useRef(undefined);
    const stop = () => {
        if (pollTimerRef.current !== undefined) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = undefined;
        }
    };
    const load = (initial = true) => {
        stop();
        if (initial)
            setLoading(true);
        openShellClient
            .getProviderRefreshStatus(providerName)
            .then((s) => {
            setStatus(s);
            if (s.some((e) => !TERMINAL_STATUSES.has(e.status))) {
                pollTimerRef.current = setTimeout(() => load(false), POLL_INTERVAL_MS);
            }
        })
            .catch(() => { })
            .finally(() => {
            if (initial)
                setLoading(false);
        });
    };
    useEffect(() => {
        if (!enabled)
            return;
        load();
        return stop;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providerName, enabled]);
    return { status, loading, refetch: () => load(false) };
}
const CredentialForm = ({ fields, values, onChange, onSave, onCancel, saving, saveLabel, error }) => {
    const canSave = fields.every((f) => !f.required || (values[f.name] ?? "") !== "");
    return (React.createElement(React.Fragment, null,
        fields.map((f) => {
            const label = !f.required && f.optionalLabel ? `${f.name} (Optional)` : f.name;
            return (React.createElement("div", { key: f.name, className: styles.fieldRow, style: { display: "flex", alignItems: "center" } },
                React.createElement("div", { className: styles.field, style: { flexGrow: 1 } },
                    React.createElement("div", { className: styles.labelWithInfo },
                        React.createElement("label", { className: styles.label }, label),
                        f.description && React.createElement(InfoTip, { title: f.name }, f.description)),
                    React.createElement("input", { type: f.secret ? "password" : "text", autoComplete: "off", className: styles.inputSmall, value: values[f.name] ?? "", onChange: (e) => {
                            // Read synchronously: the update runs after React pools the event.
                            const value = e.target.value;
                            onChange({ ...values, [f.name]: value });
                        }, placeholder: "" }))));
        }),
        React.createElement("div", { style: { display: "flex", alignItems: "flex-start" } },
            React.createElement("button", { className: styles.addBtn + (saving || !canSave ? " " + styles.disabled : ""), style: { marginRight: "10px" }, onClick: onSave, disabled: saving || !canSave, title: !canSave ? "Fill in all required values" : undefined }, saving ? "Saving…" : saveLabel),
            React.createElement("button", { className: styles.removeBtn, style: { width: "auto", height: "auto", lineHeight: "normal", padding: "5px 10px", fontSize: "11.5px" }, onClick: onCancel }, "Cancel"),
            error && React.createElement("div", { className: styles.errorMessage }, error))));
};
const materialFields = (credentialProfile) => (credentialProfile.refresh?.material ?? []).map((m) => ({
    name: m.name,
    secret: m.secret,
    required: m.required,
    description: m.description,
    optionalLabel: true
}));
const valueFields = (envVars) => envVars.map((v) => ({ name: v, secret: true, required: true }));
const strategyLabel = (credentialProfile) => (credentialProfile.refresh?.strategy ?? "static").replaceAll("_", " ");
// ── Update-mode card: configure/refresh against an existing provider ───────
const ProviderCredentialUpdateCard = ({ openShellClient, provider, credentialProfile }) => {
    const refreshable = isRefreshableCredential(credentialProfile);
    const envVars = credentialProfile.env_vars ?? [];
    const { status, loading, refetch } = useProviderRefreshStatus(openShellClient, provider.name, refreshable);
    const [entryActionLoading, setEntryActionLoading] = useState({});
    const [formKey, setFormKey] = useState(); // refreshable: env var being configured
    const [valuesOpen, setValuesOpen] = useState(false); // value credential form open
    const [formValues, setFormValues] = useState({});
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState();
    if (envVars.length === 0)
        return null;
    const saveMaterial = () => {
        if (formKey === undefined)
            return;
        const material = (credentialProfile.refresh?.material ?? []).map((m) => ({
            name: m.name,
            value: formValues[m.name] ?? ""
        }));
        setSaving(true);
        setFormError(undefined);
        openShellClient
            .configureProviderRefresh(provider.name, { credential_key: formKey, material })
            .then(() => {
            setFormKey(undefined);
            refetch();
        })
            .catch((e) => setFormError(e?.message ?? "Failed to configure refresh"))
            .finally(() => setSaving(false));
    };
    const saveValues = () => {
        const pairs = envVars.map((v) => ({ key: v, value: formValues[v] ?? "" })).filter((p) => p.value !== "");
        if (pairs.length === 0)
            return;
        setSaving(true);
        setFormError(undefined);
        openShellClient
            .updateProvider(provider.name, { credentials: pairs })
            .then(() => setValuesOpen(false))
            .catch((e) => setFormError(e?.message ?? "Failed to update credential"))
            .finally(() => setSaving(false));
    };
    const handleRotate = (credentialKey) => {
        setEntryActionLoading((prev) => ({ ...prev, [credentialKey]: "rotate" }));
        openShellClient
            .rotateProviderRefreshCredential(provider.name, credentialKey)
            .then(() => refetch())
            .catch(() => { })
            .finally(() => setEntryActionLoading((prev) => ({ ...prev, [credentialKey]: undefined })));
    };
    const handleDeleteRefresh = (credentialKey) => {
        setEntryActionLoading((prev) => ({ ...prev, [credentialKey]: "delete" }));
        openShellClient
            .deleteProviderRefreshCredential(provider.name, credentialKey)
            .then(() => refetch())
            .catch(() => { })
            .finally(() => setEntryActionLoading((prev) => ({ ...prev, [credentialKey]: undefined })));
    };
    const renderStatusCard = (entry) => {
        const entryStatus = entryActionLoading[entry.credential_key] == "rotate" ? "rotation_requested" : entry.status;
        const isTerminal = TERMINAL_STATUSES.has(entryStatus);
        const busy = !!(entryActionLoading[entry.credential_key] || !isTerminal);
        return (React.createElement("div", { key: entry.credential_key, className: styles.endpointCard, style: !isTerminal ? { opacity: 0.75 } : undefined },
            React.createElement("div", { className: styles.endpointHeader },
                React.createElement("span", { className: styles.endpointLabel + " " + styles.credentialHeaderItem }, entry.credential_key),
                React.createElement("div", { className: styles.credentialHeaderItem, style: { display: "flex", justifyContent: "right" } }, entryStatus == "configured" || entryStatus == "rotation_requested" ? (React.createElement("span", { className: styles.staticBadge }, entryStatus.replace("rotation", "refresh").replaceAll("_", " "))) : entryStatus == "error" ? (React.createElement("span", { className: styles.errorBadge }, entryStatus)) : (React.createElement("span", { className: styles.dynamicBadge },
                    entryStatus + (entry.last_refresh ? " " : ""),
                    entry.last_refresh ? React.createElement(TimeValue, { iso: entry.last_refresh }) : null)))),
            entry.last_error && (React.createElement("div", { className: styles.errorMessage, style: { marginTop: 4 } }, entry.last_error)),
            React.createElement("div", { style: { display: "flex", gap: 8, marginTop: 8 } },
                React.createElement("button", { className: styles.addBtn + (busy ? " " + styles.disabled : ""), onClick: () => handleRotate(entry.credential_key), disabled: busy }, entryActionLoading[entry.credential_key] === "rotate" ? "Refreshing…" : "Refresh Now"),
                React.createElement("button", { className: styles.removeBtn + (busy ? " " + styles.disabled : ""), style: { width: "auto", height: "auto", lineHeight: "normal", padding: "5px 10px", fontSize: "11.5px" }, onClick: () => handleDeleteRefresh(entry.credential_key), disabled: busy }, entryActionLoading[entry.credential_key] === "delete" ? "Deleting…" : "× Delete"),
                entry.next_refresh ? (React.createElement("div", { className: styles.field, style: { fontSize: 11, color: "#9b95ae", marginTop: 4, flexDirection: "row", alignItems: "center" } },
                    React.createElement("span", null, "Next Refresh: "),
                    React.createElement(TimeValue, { iso: entry.next_refresh }))) : (!isTerminal && (React.createElement("div", { style: { fontSize: 11, color: "#9b95ae", marginTop: 4, display: "flex", alignItems: "center" } }, "Refreshing\u2026"))))));
    };
    const renderPlaceholderStatusCard = (key) => {
        return (React.createElement("div", { key: key, className: styles.endpointCard, style: { opacity: 0.75 } },
            React.createElement("div", { className: styles.endpointHeader },
                React.createElement("span", { className: styles.endpointLabel + " " + styles.credentialHeaderItem }, key)),
            React.createElement("div", { style: { display: "flex", gap: 8, marginTop: 8, opacity: 0 } },
                React.createElement("button", { className: styles.addBtn + " " + styles.disabled, disabled: true }, "Loading"))));
    };
    // Value credentials (static / external / none): one card, set values directly.
    if (!refreshable) {
        return (React.createElement("div", { className: styles.endpointCard, style: { gap: "9px" } },
            React.createElement("div", { className: styles.endpointHeader },
                React.createElement("span", { className: styles.endpointLabel + " " + styles.credentialHeaderItem, title: credentialProfile.name }, credentialProfile.name),
                React.createElement("div", { className: styles.credentialHeaderItem, style: { display: "flex", justifyContent: "right", flex: "0.7" } },
                    React.createElement("span", { className: styles.staticBadge }, strategyLabel(credentialProfile)))),
            valuesOpen ? (React.createElement(CredentialForm, { fields: valueFields(envVars), values: formValues, onChange: setFormValues, onSave: saveValues, onCancel: () => {
                    setValuesOpen(false);
                    setFormError(undefined);
                }, saving: saving, saveLabel: "Save Credential", error: formError })) : (React.createElement("button", { className: styles.addBtn, style: { marginTop: 8 }, onClick: () => {
                    setValuesOpen(true);
                    setFormValues({});
                    setFormError(undefined);
                } }, "Configure"))));
    }
    return (React.createElement(React.Fragment, null, envVars.map((key) => {
        if (loading)
            return renderPlaceholderStatusCard(key);
        const entry = status.find((e) => e.credential_key === key);
        if (entry)
            return renderStatusCard(entry);
        return (React.createElement("div", { key: key, className: styles.endpointCard, style: { gap: "9px" } },
            React.createElement("div", { className: styles.endpointHeader },
                React.createElement("span", { className: styles.endpointLabel + " " + styles.credentialHeaderItem, title: key }, key),
                React.createElement("div", { className: styles.credentialHeaderItem, style: { display: "flex", justifyContent: "right", flex: "0.9" } },
                    React.createElement("span", { className: styles.staticBadge }, "FROM " + strategyLabel(credentialProfile)))),
            formKey === key ? (React.createElement(CredentialForm, { fields: materialFields(credentialProfile), values: formValues, onChange: setFormValues, onSave: saveMaterial, onCancel: () => {
                    setFormKey(undefined);
                    setFormError(undefined);
                }, saving: saving, saveLabel: "Save Configuration", error: formError })) : (React.createElement("button", { className: styles.addBtn, style: { marginTop: 8 }, onClick: () => {
                    setFormKey(key);
                    setFormValues({});
                    setFormError(undefined);
                } }, "Configure"))));
    })));
};
// ── Create-mode card: stash configuration as a pending contribution ────────
const ProviderCredentialPendingCard = ({ credentialProfile, onContributionChange }) => {
    const refreshable = isRefreshableCredential(credentialProfile);
    const envVars = credentialProfile.env_vars ?? [];
    const [pendingMaterial, setPendingMaterial] = useState({});
    const [pendingValues, setPendingValues] = useState();
    const [formKey, setFormKey] = useState(); // refreshable: env var being edited
    const [valuesOpen, setValuesOpen] = useState(false);
    const [formValues, setFormValues] = useState({});
    if (envVars.length === 0)
        return null;
    const report = (material, values) => onContributionChange(credentialProfile.name, buildContribution(refreshable, material, values));
    const saveMaterial = () => {
        if (formKey === undefined)
            return;
        const material = (credentialProfile.refresh?.material ?? []).map((m) => ({
            name: m.name,
            value: formValues[m.name] ?? ""
        }));
        const next = { ...pendingMaterial, [formKey]: material };
        setPendingMaterial(next);
        report(next, pendingValues);
        setFormKey(undefined);
    };
    const removeMaterial = (key) => {
        const next = { ...pendingMaterial };
        delete next[key];
        setPendingMaterial(next);
        report(next, pendingValues);
    };
    const saveValues = () => {
        const pairs = envVars.map((v) => ({ key: v, value: formValues[v] ?? "" })).filter((p) => p.value !== "");
        if (pairs.length === 0)
            return;
        setPendingValues(pairs);
        report(pendingMaterial, pairs);
        setValuesOpen(false);
    };
    const removeValues = () => {
        setPendingValues(undefined);
        report(pendingMaterial, undefined);
    };
    const pendingActions = (onEdit, onRemove) => (React.createElement("div", { style: { display: "flex", gap: 8 } },
        React.createElement("button", { className: styles.addBtn, onClick: onEdit }, "Edit"),
        React.createElement("button", { className: styles.removeBtn, style: { width: "auto", height: "auto", lineHeight: "normal", padding: "5px 10px", fontSize: "11.5px" }, onClick: onRemove }, "\u00D7 Remove")));
    // Value credentials: one card.
    if (!refreshable) {
        return (React.createElement("div", { className: styles.endpointCard, style: { gap: "9px" } },
            React.createElement("div", { className: styles.endpointHeader },
                React.createElement("span", { className: styles.endpointLabel + " " + styles.credentialHeaderItem, title: credentialProfile.name }, credentialProfile.name),
                React.createElement("div", { className: styles.credentialHeaderItem, style: { display: "flex", justifyContent: "right", flex: "0.7", gap: 6 } },
                    React.createElement("span", { className: styles.staticBadge }, strategyLabel(credentialProfile)))),
            valuesOpen ? (React.createElement(CredentialForm, { fields: valueFields(envVars), values: formValues, onChange: setFormValues, onSave: saveValues, onCancel: () => setValuesOpen(false), saveLabel: "Save Credential" })) : pendingValues ? (pendingActions(() => {
                setValuesOpen(true);
                setFormValues(Object.fromEntries(envVars.map((v) => [v, pendingValues.find((e) => e.key === v)?.value ?? ""])));
            }, removeValues)) : (React.createElement("button", { className: styles.addBtn, style: { marginTop: 8 }, onClick: () => {
                    setValuesOpen(true);
                    setFormValues({});
                } }, "Configure"))));
    }
    // Refreshable credentials: one card per env var.
    return (React.createElement(React.Fragment, null, envVars.map((key) => {
        const pending = pendingMaterial[key];
        return (React.createElement("div", { key: key, className: styles.endpointCard, style: { gap: "9px" } },
            React.createElement("div", { className: styles.endpointHeader },
                React.createElement("span", { className: styles.endpointLabel + " " + styles.credentialHeaderItem, title: key }, key),
                React.createElement("div", { className: styles.credentialHeaderItem, style: { display: "flex", justifyContent: "right", flex: "0.9", gap: 6 } },
                    React.createElement("span", { className: styles.staticBadge }, "FROM " + strategyLabel(credentialProfile)))),
            formKey === key ? (React.createElement(CredentialForm, { fields: materialFields(credentialProfile), values: formValues, onChange: setFormValues, onSave: saveMaterial, onCancel: () => setFormKey(undefined), saveLabel: "Save Configuration" })) : pending ? (pendingActions(() => {
                setFormKey(key);
                setFormValues(Object.fromEntries(pending.map((m) => [m.name, m.value])));
            }, () => removeMaterial(key))) : (React.createElement("button", { className: styles.addBtn, style: { marginTop: 8 }, onClick: () => {
                    setFormKey(key);
                    setFormValues({});
                } }, "Configure"))));
    })));
};
// ── Manual key/value editor (generic providers) ───────────────────────────
const CredentialEditor = ({ credential, onChange, onRemove, mode, type }) => {
    const update = (partial) => onChange({ ...credential, ...partial });
    return (React.createElement("div", { className: styles.endpointCard },
        React.createElement("div", { className: styles.fieldRow, style: { display: "flex", alignItems: "center" } },
            React.createElement("div", { className: styles.field, style: { flexGrow: 1 } },
                React.createElement("div", { className: styles.labelWithInfo },
                    React.createElement("label", { className: styles.label }, "Key"),
                    React.createElement(InfoTip, { title: "Key" }, type === "credential"
                        ? "A credential key name or environment variable the agent needs to authenticate (eg. OPENAI_API_KEY)."
                        : "A configuration value or environment variable the agent needs to authenticate (eg. OPENAI_BASE_URL).")),
                React.createElement("input", { className: styles.inputSmall + (mode === "create" ? "" : " " + styles.disabled), value: credential.key, onChange: (e) => update({ key: e.target.value }), placeholder: type === "credential" ? "API_KEY" : "CONFIG_VALUE", disabled: mode !== "create" })),
            React.createElement("div", { className: styles.field, style: { flexGrow: 1 } },
                React.createElement("div", { className: styles.labelWithInfo },
                    React.createElement("label", { className: styles.label }, "Value"),
                    React.createElement(InfoTip, { title: "Value" }, "The value that contains potentially secret information.")),
                React.createElement("input", { type: type === "credential" ? "password" : "text", autoComplete: "off", className: styles.inputSmall, value: credential.value, onChange: (e) => update({ value: e.target.value }), placeholder: "********" })),
            mode === "create" && (React.createElement("button", { className: styles.removeBtn, onClick: onRemove, title: "Remove" }, "\u00D7")))));
};
// ── Provider entry editor (one provider, create or update) ─────────────────
const ProviderEntryEditor = ({ name, provider, isLoading, onOpen, onChange, onRemove, onSubmit, mode, errorMessage, openShellClient, profileFilter, onCreated }) => {
    const [open, setOpen] = useState(onOpen === undefined);
    const [profiles, setProfiles] = useState([]);
    const [providerProfile, setProviderProfile] = useState();
    // Lazy create flow: per-credential contributions reported up by the cards,
    // applied together when +Create is pressed. Keyed by credential name.
    const [contributions, setContributions] = useState({});
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState();
    const handleContributionChange = (credentialName, contribution) => {
        setContributions((prev) => {
            if (contribution === undefined) {
                const next = { ...prev };
                delete next[credentialName];
                return next;
            }
            return { ...prev, [credentialName]: contribution };
        });
    };
    useEffect(() => {
        if (open && onOpen) {
            onOpen();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, name]);
    useEffect(() => {
        if (!open || mode !== "create" || !openShellClient)
            return;
        openShellClient.getProviderProfiles().then((p) => {
            if (profileFilter !== undefined) {
                p = p.filter(profileFilter);
            }
            setProfiles(p);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    // Load the full provider profile (with credentials) whenever a non-generic
    // profile is selected, in both create and update mode. Cleared for generic.
    useEffect(() => {
        // Switching profile changes the set of credentials, so drop pending work.
        setContributions({});
        setCreateError(undefined);
        if (!open || !openShellClient)
            return;
        if (!provider?.type || provider.type === "generic") {
            setProviderProfile(undefined);
            return;
        }
        openShellClient
            .getProviderProfile(provider.type)
            .then((profile) => setProviderProfile(profile))
            .catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, provider?.type]);
    const handleChange = (update) => {
        if (provider === undefined)
            return;
        onChange({ ...provider, ...update });
    };
    const updateCredential = (i, cred) => {
        if (provider === undefined)
            return;
        handleChange({ credentials: provider.credentials.map((c, j) => (j === i ? cred : c)) });
    };
    const removeCredential = (i) => {
        if (provider === undefined)
            return;
        handleChange({ credentials: provider.credentials.filter((_, j) => j !== i) });
    };
    const addCredential = () => {
        if (provider === undefined)
            return;
        handleChange({ credentials: [...provider.credentials, { key: "", value: "" }] });
    };
    const updateConfig = (i, cred) => {
        if (provider === undefined)
            return;
        handleChange({ configs: provider.configs.map((c, j) => (j === i ? cred : c)) });
    };
    const removeConfig = (i) => {
        if (provider === undefined)
            return;
        handleChange({ configs: provider.configs.filter((_, j) => j !== i) });
    };
    const addConfig = () => {
        if (provider === undefined)
            return;
        handleChange({ configs: [...provider.configs, { key: "", value: "" }] });
    };
    const isInvalidName = (n) => /[^a-z0-9-]/.test(n);
    if (errorMessage === undefined && provider !== undefined && isInvalidName(provider.name)) {
        errorMessage = `Invalid name: '${provider.name}'. Names can only contain lowercase letters, numbers and dashes.`;
    }
    // A profile-based provider configures its credentials through the per-credential
    // cards; the manual credentials list is hidden.
    const isProfileBased = providerProfile?.credentials !== undefined && openShellClient !== undefined;
    // Card-driven creation needs a valid (non-empty, well-formed) provider name.
    const hasValidName = provider !== undefined && provider.name.length > 0 && !isInvalidName(provider.name);
    const isValidProvider = errorMessage === undefined &&
        provider !== undefined &&
        provider.name.length > 0 &&
        provider.credentials.length > 0 &&
        !isInvalidName(provider.name) &&
        (mode === "create"
            ? provider.credentials.every((c) => c.key.length > 0 && c.value.length > 0) &&
                provider.configs.every((c) => c.key.length > 0 && c.value.length > 0)
            : provider.credentials.some((c) => c.key.length > 0 && c.value.length > 0) ||
                provider.configs.some((c) => c.key.length > 0 && c.value.length > 0));
    // +Create is enabled for a profile once the name is valid, every required
    // credential is configured, and at least one credential is configured.
    const requiredCredentialNames = (providerProfile?.credentials ?? []).filter((c) => c.required).map((c) => c.name);
    const allRequiredConfigured = requiredCredentialNames.every((n) => contributions[n] !== undefined);
    const canProfileCreate = hasValidName && Object.keys(contributions).length > 0 && allRequiredConfigured;
    const canCreate = isProfileBased ? canProfileCreate : isValidProvider;
    // Unified create path: build a {credentials, configures} plan for either a
    // profile (from pending contributions) or a generic provider (from the manual
    // credential fields), create it, then run any refresh configures.
    const handleCreate = () => {
        if (!openShellClient || provider === undefined)
            return;
        const plan = isProfileBased
            ? Object.values(contributions).reduce((acc, c) => ({
                credentials: acc.credentials.concat(c.credentials),
                configures: acc.configures.concat(c.configures)
            }), { credentials: [], configures: [] })
            : { credentials: provider.credentials.filter((c) => c.key.length > 0 && c.value.length > 0), configures: [] };
        if (plan.credentials.length === 0)
            return;
        setCreating(true);
        setCreateError(undefined);
        openShellClient
            .createProvider({
            name: provider.name,
            type: provider.type,
            credentials: plan.credentials,
            configs: provider.configs
        })
            .then((created) => Promise.allSettled(plan.configures.map((cfg) => openShellClient.configureProviderRefresh(created.name, cfg))).then(() => onCreated?.(created)))
            .catch((e) => setCreateError(e?.message ?? "Failed to create provider"))
            .finally(() => setCreating(false));
    };
    return (React.createElement("div", { className: styles.policyEntry },
        onOpen && (React.createElement("button", { className: styles.policyEntryHeader, onClick: () => setOpen((v) => !v) },
            React.createElement("span", { className: styles.policyEntryName },
                React.createElement("span", { className: open ? styles.chevronOpen : styles.chevron }, "\u25B8"),
                name),
            isLoading && React.createElement("span", { className: styles.policyEntryMeta }, "loading..."))),
        open && provider !== undefined && (React.createElement("div", { className: styles.policyEntryBody },
            React.createElement("div", { className: styles.fieldRow },
                React.createElement("div", { className: styles.field },
                    React.createElement("div", { className: styles.labelWithInfo },
                        React.createElement("label", { className: styles.label }, "Name"),
                        React.createElement(InfoTip, { title: "Provider Name" }, "A unique identifier for this credential provider (e.g. \"my-claude\" or \"my-api\"). Used internally to reference the provider. The name can only include alphanumeric values and dashes.")),
                    React.createElement("input", { className: styles.inputSmall + (mode === "create" ? "" : " " + styles.disabled), value: provider.name, onChange: (e) => handleChange({ name: e.target.value }), disabled: mode !== "create" })),
                React.createElement("div", { className: styles.field },
                    React.createElement("div", { className: styles.labelWithInfo },
                        React.createElement("label", { className: styles.label }, "Profile"),
                        React.createElement(InfoTip, { title: "Profile" }, "The profile that defines the template for these credentials. Defaults to \"generic\".")),
                    React.createElement("select", { className: styles.select + " " + styles.inputSmall + (mode === "create" ? "" : " " + styles.disabled), value: provider.type, onChange: (e) => handleChange({ type: e.target.value }), disabled: mode !== "create" }, [
                        React.createElement("option", { key: "generic", value: "generic" }, "generic")
                    ].concat((profiles.length === 0 ? [{ id: provider.type }] : profiles).map((profile) => (React.createElement("option", { key: profile.id, value: profile.id }, profile.id))))))),
            React.createElement("div", { className: styles.labelWithInfo },
                React.createElement("span", { className: styles.subLabel }, "Credentials"),
                React.createElement(InfoTip, { title: "Credentials" }, "The credentials that this provider will make available for the AI agent to use. The agent running in the sandbox never sees these values.")),
            isProfileBased && openShellClient && providerProfile?.credentials
                ? providerProfile.credentials.map((credentialProfile) => mode === "update" ? (React.createElement(ProviderCredentialUpdateCard, { key: credentialProfile.name, openShellClient: openShellClient, provider: provider, credentialProfile: credentialProfile })) : (React.createElement(ProviderCredentialPendingCard, { key: credentialProfile.name, credentialProfile: credentialProfile, onContributionChange: handleContributionChange })))
                : provider.credentials.map((c, i) => (React.createElement(CredentialEditor, { key: `${i}_${isLoading}`, credential: c, onChange: (cred) => updateCredential(i, cred), onRemove: () => removeCredential(i), mode: mode, type: "credential" }))),
            mode === "create" && !isProfileBased && (React.createElement("button", { className: styles.addBtn, onClick: addCredential }, "+ Add Credential")),
            providerProfile?.inference_capable && (mode === "create" || provider.configs.length > 0) && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: styles.labelWithInfo },
                    React.createElement("span", { className: styles.subLabel }, "Configuration Values"),
                    React.createElement(InfoTip, { title: "Configuration Values" }, "The configuration values that this provider will make available for the AI agent to use. The agent running in the sandbox never sees these values.")),
                provider.configs.map((c, i) => (React.createElement(CredentialEditor, { key: `${i}_${isLoading}`, credential: c, onChange: (cred) => updateConfig(i, cred), onRemove: () => removeConfig(i), mode: mode, type: "config" }))),
                mode === "create" && (React.createElement("button", { className: styles.addBtn, onClick: addConfig }, "+ Add Config")))),
            React.createElement("div", { style: { display: "flex", alignItems: "flex-start" } },
                mode === "create" ? (React.createElement("button", { className: styles.addBtn + (canCreate && !creating ? "" : " " + styles.disabled), style: { marginRight: "10px" }, onClick: handleCreate, disabled: !canCreate || creating, title: !canCreate ? "Enter a valid name and configure all required credentials" : undefined }, creating ? "Creating…" : "+ Create")) : (!isProfileBased && (React.createElement("button", { className: styles.addBtn + (isValidProvider && !isLoading ? "" : " " + styles.disabled), style: { marginRight: "10px" }, onClick: onSubmit, disabled: !isValidProvider || isLoading }, "+ Update"))),
                onRemove && (React.createElement("button", { className: styles.removeBtn + (!isLoading ? "" : " " + styles.disabled), style: {
                        width: "auto",
                        height: "auto",
                        lineHeight: "normal",
                        padding: "5px 10px",
                        fontSize: "11.5px",
                        marginRight: "10px"
                    }, onClick: onRemove, disabled: isLoading }, mode === "create" ? "x Cancel" : "x Delete")),
                errorMessage && React.createElement("div", { className: styles.errorMessage }, errorMessage),
                createError && React.createElement("div", { className: styles.errorMessage }, createError))))));
};
export const ProviderCreator = ({ defaultProviderFactory, onAddClick, onClear, openShellClient, onCreated, profileFilter }) => {
    const [pendingProvider, setPendingProvider] = useState();
    const [errorMessage, setErrorMessage] = useState();
    return pendingProvider === undefined ? (React.createElement("div", { style: { display: "flex" } },
        React.createElement("button", { style: { marginRight: "10px" }, className: styles.addBtn, onClick: () => {
                setPendingProvider(defaultProviderFactory());
                onAddClick?.();
            } }, "+ Add provider"))) : (React.createElement(ProviderEntryEditor, { name: pendingProvider.name, provider: pendingProvider, onChange: (p) => {
            setPendingProvider(p);
            const hasDuplicateKeys = new Set(p.credentials.map((c) => c.key)).size !== p.credentials.length;
            setErrorMessage(hasDuplicateKeys ? "All provider keys must be unique" : undefined);
        }, onRemove: () => {
            setPendingProvider(undefined);
            onClear?.();
        }, mode: "create", errorMessage: errorMessage, openShellClient: openShellClient, onCreated: (created) => {
            onCreated?.(created);
            setPendingProvider(undefined);
            onClear?.();
        }, profileFilter: profileFilter }));
};
export const ProviderEditor = ({ openShellClient }) => {
    const [providerList, setProviderList] = useState([]);
    const [providerDetails, setProviderDetails] = useState({});
    const [loadingProviders, setLoadingProviders] = useState({});
    const refreshProviders = () => openShellClient.getProviders().then(setProviderList);
    const cacheProviderDetail = (provider) => setProviderDetails((prev) => ({ ...prev, [provider.name]: provider }));
    const setProviderIsLoading = (name, isLoading) => setLoadingProviders((prev) => ({ ...prev, [name]: isLoading }));
    useEffect(() => {
        refreshProviders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const loadProviderDetails = (name) => {
        if (providerDetails[name] !== undefined)
            return;
        setProviderIsLoading(name, true);
        openShellClient
            .getProvider(name)
            .then(cacheProviderDetail)
            .finally(() => setProviderIsLoading(name, false));
    };
    return (React.createElement(React.Fragment, null,
        providerList.map((provider) => (React.createElement(ProviderEntryEditor, { key: provider.name, name: provider.name, isLoading: loadingProviders[provider.name], provider: providerDetails[provider.name], onChange: cacheProviderDetail, onOpen: () => loadProviderDetails(provider.name), onSubmit: () => {
                const detail = providerDetails[provider.name];
                if (detail === undefined)
                    return;
                const credentials = detail.credentials.filter((x) => x.value.length > 0);
                if (credentials.length == 0)
                    return;
                setProviderIsLoading(provider.name, true);
                openShellClient
                    .updateProvider(provider.name, { credentials })
                    .then(cacheProviderDetail)
                    .finally(() => setProviderIsLoading(provider.name, false));
            }, onRemove: () => {
                setProviderIsLoading(provider.name, true);
                openShellClient
                    .deleteProvider(provider.name)
                    .then(() => refreshProviders())
                    .finally(() => setProviderIsLoading(provider.name, false));
            }, mode: "update", openShellClient: openShellClient }))),
        React.createElement(ProviderCreator, { defaultProviderFactory: () => ({ name: "", type: "generic", credentials: [], configs: [] }), onCreated: (created) => {
                setProviderList((prev) => prev.some((p) => p.name === created.name) ? prev : prev.concat([{ name: created.name, type: created.type }]));
                cacheProviderDetail(created);
            }, openShellClient: openShellClient })));
};
